/**
 * Intake — the thirty-second path from "I just saw one" to "it is in the system".
 *
 * ── The one number that matters ──────────────────────────────────────────────
 *
 * PASTE URL has to be completable in under thirty seconds, one-handed, standing
 * up, possibly with no signal. Everything on this screen is arranged around
 * that: the URL field is the first thing, focused on arrival; there is a PASTE
 * button so the clipboard costs one tap instead of a long-press and a menu; and
 * SAVE does not wait for the network — it hands the write to `outbox.ts` and
 * closes. QUICK ADD is behind one toggle and is the fallback for a structure in
 * a yard with no listing at all.
 *
 * ── We do not open the page, and that is not a bug ───────────────────────────
 *
 * `docs/SOURCE_COMPLIANCE.md` is binding: ClasificadosOnline, Facebook
 * Marketplace and Craigslist are all CLOSED to automated collection, and the
 * paste-URL path is named there explicitly — no server-side fetch, not even
 * from a link a person handed us. So the link is STORED, not visited.
 *
 * That has to feel deliberate rather than broken, because the alternative is
 * somebody pasting a Marketplace link, seeing no title come back, deciding the
 * feature is faulty, and filing a bug against the one behaviour that keeps this
 * project on the right side of a robots file. Hence the line under the field
 * saying what was saved and why, and the optional "what you saw" box: two
 * seconds of typing beats a scraper we are not allowed to write.
 *
 * ── Strings ──────────────────────────────────────────────────────────────────
 *
 * English and Puerto Rican Spanish, held here rather than in `src/lib/i18n.ts`,
 * which another work unit owns in this build. `ES` is typed as
 * `Record<CopyKey, string>`, so a missing translation is a compile error in
 * exactly the way the shared table's parity test achieves. These keys move into
 * `STRINGS` when the two units are merged.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocale, type Locale } from '../lib/i18n'
import { navigate, useRoute } from '../lib/route'
import Icon from '../components/Icon'
import * as outbox from '../lib/outbox'
import * as photoQueue from '../lib/photoQueue'
import { ImagePrepError, prepareImage } from '../lib/image'
import { parseDollarsToCents } from '../../shared/money'
import { STRUCTURE_TYPES } from '../../shared/listing'

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ────────────────────────────────────────────────────────────────────────── */

const EN = {
  title: 'Add a lead',
  tabUrl: 'Paste a link',
  tabQuick: 'Quick add',
  urlLabel: 'Listing link',
  urlPlaceholder: 'Paste the link here',
  paste: 'Paste',
  save: 'Save',
  saving: 'Saving…',
  noFetch: 'We save the link and whatever you paste with it. We do not open the page — Facebook, Clasificados and Craigslist all ask not to be read by software, and we honour that.',
  sawLabel: 'What you saw (optional)',
  sawHint: 'Title, asking price, anything the link will not tell us later.',
  askingPrice: 'Asking price',
  quickTitle: 'What is it',
  quickTitlePlaceholder: '28ft travel trailer, needs a roof',
  where: 'Where is it',
  wherePlaceholder: 'Ceiba, near the ferry',
  sellerName: 'Seller',
  sellerPhone: 'Phone',
  structure: 'Kind of structure',
  notes: 'Notes or pasted text',
  photos: 'Photos',
  addPhoto: 'Add a photo',
  photoWorking: 'Preparing…',
  remove: 'Remove',
  queued: 'Saved. It will sync when there is a signal.',
  queuedNow: 'Saved.',
  duplicate: 'That one is already in the queue.',
  needUrl: 'Paste a link first.',
  needSomething: 'Add a title, a price or a photo.',
  badUrl: 'That does not look like a link.',
  unsent: 'waiting to send',
  offline: 'No connection — this is being kept on the phone.',
  fromShare: 'Shared from another app.',
  another: 'Add another',
  done: 'Done',
  photoFull: 'The photo queue is full.',
  photoBad: 'That photo could not be read.',
  structureUnset: 'Not sure',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Añadir un prospecto',
  tabUrl: 'Pegar un enlace',
  tabQuick: 'Añadir rápido',
  urlLabel: 'Enlace del anuncio',
  urlPlaceholder: 'Pega el enlace aquí',
  paste: 'Pegar',
  save: 'Guardar',
  saving: 'Guardando…',
  noFetch: 'Guardamos el enlace y lo que pegues con él. No abrimos la página — Facebook, Clasificados y Craigslist piden que ningún programa las lea, y lo respetamos.',
  sawLabel: 'Lo que viste (opcional)',
  sawHint: 'Título, precio, cualquier cosa que el enlace no nos vaya a decir después.',
  askingPrice: 'Precio pedido',
  quickTitle: 'Qué es',
  quickTitlePlaceholder: 'Tráiler de 28 pies, necesita techo',
  where: 'Dónde está',
  wherePlaceholder: 'Ceiba, cerca de la lancha',
  sellerName: 'Vendedor',
  sellerPhone: 'Teléfono',
  structure: 'Tipo de estructura',
  notes: 'Notas o texto pegado',
  photos: 'Fotos',
  addPhoto: 'Añadir una foto',
  photoWorking: 'Preparando…',
  remove: 'Quitar',
  queued: 'Guardado. Se enviará cuando haya señal.',
  queuedNow: 'Guardado.',
  duplicate: 'Ese ya está en la fila.',
  needUrl: 'Pega un enlace primero.',
  needSomething: 'Añade un título, un precio o una foto.',
  badUrl: 'Eso no parece un enlace.',
  unsent: 'esperando para enviarse',
  offline: 'Sin conexión — esto se queda guardado en el teléfono.',
  fromShare: 'Compartido desde otra app.',
  another: 'Añadir otro',
  done: 'Listo',
  photoFull: 'La fila de fotos está llena.',
  photoBad: 'No se pudo leer esa foto.',
  structureUnset: 'No estoy seguro',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

/** Structure labels stay in English keys with a Spanish face, like the rest of the domain. */
const STRUCTURE_LABEL: Record<Locale, Record<string, string>> = {
  en: {
    travel_trailer: 'Travel trailer', fifth_wheel: 'Fifth wheel', motorhome: 'Motorhome',
    mobile_home: 'Mobile home', modular_home: 'Modular home', park_model: 'Park model',
    container: 'Container', cargo_trailer: 'Cargo trailer', tiny_house: 'Tiny house',
    casita: 'Casita', other: 'Other',
  },
  'es-PR': {
    travel_trailer: 'Tráiler de viaje', fifth_wheel: 'Fifth wheel', motorhome: 'Casa rodante',
    mobile_home: 'Casa móvil', modular_home: 'Casa modular', park_model: 'Park model',
    container: 'Contenedor', cargo_trailer: 'Tráiler de carga', tiny_house: 'Casa pequeña',
    casita: 'Casita', other: 'Otro',
  },
}

/* ────────────────────────────────────────────────────────────────────────────
 * Small helpers
 * ────────────────────────────────────────────────────────────────────────── */

function newId(): string {
  // `randomUUID` needs a secure context; a phone on http in the field is not
  // one, and an id that throws is worse than a slightly weaker random one.
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  } catch {
    /* falls through to the manual form below */
  }
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

/** Looks like a link, without pretending to be a full URL parser. */
function looksLikeUrl(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
  try {
    const parsed = new URL(withScheme)
    if (!parsed.hostname.includes('.')) return null
    return parsed.toString()
  } catch {
    return null
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * Styles
 *
 * Inline, against the tokens `index.css` already defines. That file belongs to
 * another work unit in this build, and two agents editing one stylesheet is how
 * a rule disappears without anybody noticing.
 * ────────────────────────────────────────────────────────────────────────── */

const S = {
  page: { padding: 16, paddingBottom: 96, maxWidth: 640, margin: '0 auto' } as const,
  tabs: { display: 'flex', gap: 8, marginBottom: 16 } as const,
  tab: (on: boolean) => ({
    flex: 1, minHeight: 48, borderRadius: 'var(--radius)', border: '1px solid var(--line)',
    background: on ? 'var(--brand)' : 'var(--surface)', color: on ? 'var(--brand-ink)' : 'var(--ink)',
    fontWeight: on ? 700 : 500, fontSize: 16, cursor: 'pointer',
  }) as const,
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, marginTop: 14 } as const,
  input: {
    width: '100%', minHeight: 52, fontSize: 17, padding: '12px 14px', borderRadius: 'var(--radius)',
    border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', boxSizing: 'border-box',
  } as const,
  area: {
    width: '100%', minHeight: 96, fontSize: 16, padding: '12px 14px', borderRadius: 'var(--radius)',
    border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', boxSizing: 'border-box',
    resize: 'vertical',
  } as const,
  note: { fontSize: 13, lineHeight: 1.45, color: 'var(--muted)', marginTop: 8 } as const,
  row: { display: 'flex', gap: 8, alignItems: 'stretch' } as const,
  ghost: {
    minHeight: 52, padding: '0 16px', borderRadius: 'var(--radius)', border: '1px solid var(--line)',
    background: 'var(--surface)', color: 'var(--ink)', fontSize: 15, fontWeight: 600, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 6,
  } as const,
  primary: (busy: boolean) => ({
    width: '100%', minHeight: 56, marginTop: 20, borderRadius: 'var(--radius)', border: 'none',
    background: busy ? 'var(--muted)' : 'var(--brand)', color: 'var(--brand-ink)',
    fontSize: 18, fontWeight: 800, cursor: busy ? 'progress' : 'pointer',
  }) as const,
  banner: (tone: 'ok' | 'warn') => ({
    padding: '10px 12px', borderRadius: 'var(--radius)', fontSize: 14, marginBottom: 12,
    background: tone === 'ok' ? 'var(--score-strong-bg)' : 'var(--warn-bg)',
    color: tone === 'ok' ? 'var(--score-strong)' : 'var(--warn)',
  }) as const,
  thumbs: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 } as const,
  thumb: { width: 84, height: 84, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--line)' } as const,
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 } as const,
  chip: (on: boolean) => ({
    minHeight: 40, padding: '0 12px', borderRadius: 999, fontSize: 14,
    border: `1px solid ${on ? 'var(--brand-strong)' : 'var(--line)'}`,
    background: on ? 'var(--brand-soft)' : 'var(--surface)', color: 'var(--ink)', cursor: 'pointer',
  }) as const,
}

interface Thumb {
  key: string
  url: string
  name: string
}

/* ────────────────────────────────────────────────────────────────────────────
 * The screen
 * ────────────────────────────────────────────────────────────────────────── */

export default function Intake() {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])
  const route = useRoute()

  // The share sheet arrives as a GET with title/text/url (the manifest declares
  // method GET for exactly this reason — a POST target would render the API's
  // JSON response in the browser instead of opening the app).
  const shared = useMemo(() => ({
    url: route.query.get('url') ?? '',
    title: route.query.get('title') ?? '',
    text: route.query.get('text') ?? '',
  }), [route.query])
  const fromShare = Boolean(shared.url || shared.title || shared.text)

  const [mode, setMode] = useState<'url' | 'quick'>('url')
  const [url, setUrl] = useState(shared.url)
  const [saw, setSaw] = useState(shared.text)
  const [title, setTitle] = useState(shared.title)
  const [price, setPrice] = useState('')
  const [where, setWhere] = useState('')
  const [sellerName, setSellerName] = useState('')
  const [sellerPhone, setSellerPhone] = useState('')
  const [structure, setStructure] = useState('')
  const [notes, setNotes] = useState('')
  const [thumbs, setThumbs] = useState<Thumb[]>([])
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null)
  const [queue, setQueue] = useState(() => outbox.getState())

  const urlRef = useRef<HTMLInputElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  // One id per FORM, rotated only after a successful enqueue. Two taps on SAVE
  // therefore carry the same client_id, the second enqueue is refused, and the
  // server would refuse it a second time anyway. Belt and braces, on the one
  // button most likely to be pressed twice by someone who is not sure it worked.
  const clientId = useRef(newId())

  useEffect(() => {
    outbox.start()
    return outbox.subscribe(() => setQueue(outbox.getState()))
  }, [])

  // Autofocus, but not when the share sheet already filled the field — landing
  // in a full field with the keyboard up hides the SAVE button on a phone.
  useEffect(() => {
    if (mode === 'url' && !shared.url) urlRef.current?.focus()
  }, [mode, shared.url])

  // Object URLs are a leak until revoked, and this screen makes one per photo.
  useEffect(() => () => {
    for (const thumb of thumbs) URL.revokeObjectURL(thumb.url)
    // Deliberately reads `thumbs` at unmount only; the cleanup for an individual
    // removal is done in `dropPhoto`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const addPhotos = useCallback(async (files: FileList | null) => {
    if (!files?.length) return
    setBusy(true)
    for (const file of Array.from(files)) {
      try {
        const prepared = await prepareImage(file)
        const key = newId()
        await photoQueue.put({
          key,
          blob: prepared.blob,
          width: prepared.width,
          height: prepared.height,
          opportunityId: null,
          itemKey: null,
        })
        setThumbs((current) => [...current, { key, url: URL.createObjectURL(prepared.blob), name: prepared.originalName }])
      } catch (err) {
        const text = err instanceof ImagePrepError || err instanceof photoQueue.PhotoQueueFull
          ? err.message
          : t('photoBad')
        setMessage({ tone: 'warn', text })
      }
    }
    setBusy(false)
  }, [t])

  const dropPhoto = useCallback(async (key: string) => {
    setThumbs((current) => {
      const hit = current.find((th) => th.key === key)
      if (hit) URL.revokeObjectURL(hit.url)
      return current.filter((th) => th.key !== key)
    })
    await photoQueue.remove(key)
  }, [])

  const resetForm = useCallback(() => {
    setUrl('')
    setSaw('')
    setTitle('')
    setPrice('')
    setWhere('')
    setSellerName('')
    setSellerPhone('')
    setStructure('')
    setNotes('')
    setThumbs((current) => {
      for (const thumb of current) URL.revokeObjectURL(thumb.url)
      return []
    })
    clientId.current = newId()
  }, [])

  const submit = useCallback(async () => {
    if (busy) return
    setMessage(null)

    const priceCents = parseDollarsToCents(price)
    let path: string
    let body: Record<string, unknown>
    let photos: string[] = []

    if (mode === 'url') {
      const clean = looksLikeUrl(url)
      if (!clean) {
        setMessage({ tone: 'warn', text: url.trim() ? t('badUrl') : t('needUrl') })
        urlRef.current?.focus()
        return
      }
      path = '/api/intake/url'
      body = {
        url: clean,
        idempotencyKey: clientId.current,
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(saw.trim() ? { description: saw.trim() } : {}),
        ...(priceCents !== null ? { priceCents } : {}),
      }
    } else {
      if (!title.trim() && priceCents === null && thumbs.length === 0) {
        setMessage({ tone: 'warn', text: t('needSomething') })
        return
      }
      path = '/api/intake/quick'
      photos = thumbs.map((th) => th.key)
      body = {
        idempotencyKey: clientId.current,
        title: title.trim() || null,
        description: notes.trim() || null,
        priceCents,
        locationText: where.trim() || null,
        ...(sellerName.trim() || sellerPhone.trim()
          ? { seller: { name: sellerName.trim() || null, phone: sellerPhone.trim() || null } }
          : {}),
        ...(structure ? { attributes: { structure_type: structure } } : {}),
      }
    }

    setBusy(true)
    const accepted = await outbox.enqueue({
      id: clientId.current,
      kind: 'intake',
      method: 'POST',
      path,
      body,
      photos,
      photoField: photos.length ? 'images' : undefined,
      purpose: 'listing',
      // A short label for the unsent list. Never the note body, never a phone
      // number — §14.2, and this string is rendered in a list somebody may show
      // to another person.
      label: mode === 'url' ? 'Pasted link' : (title.trim().slice(0, 40) || 'Quick add'),
      opportunityId: null,
    })
    setBusy(false)

    if (!accepted) {
      setMessage({ tone: 'warn', text: t('duplicate') })
      return
    }
    setMessage({ tone: 'ok', text: outbox.getState().online ? t('queuedNow') : t('queued') })
    resetForm()
  }, [busy, mode, url, title, saw, price, notes, where, sellerName, sellerPhone, structure, thumbs, t, resetForm])

  const paste = useCallback(async () => {
    try {
      const text = await navigator.clipboard?.readText?.()
      if (text) {
        setUrl(text.trim())
        urlRef.current?.focus()
      }
    } catch {
      // Firefox has no readText for content scripts and Safari asks first. The
      // field is still there to paste into by hand, so there is nothing to say.
    }
  }, [])

  return (
    <div style={S.page}>
      <h1 style={{ fontSize: 22, fontWeight: 800, margin: '4px 0 14px' }}>{t('title')}</h1>

      {fromShare && <div style={S.banner('ok')}>{t('fromShare')}</div>}
      {!queue.online && <div style={S.banner('warn')}>{t('offline')}</div>}
      {queue.pending > 0 && (
        <div style={S.note}>{queue.pending} {t('unsent')}</div>
      )}
      {message && <div style={S.banner(message.tone)}>{message.text}</div>}

      <div style={S.tabs} role="tablist" aria-label={t('title')}>
        <button type="button" role="tab" aria-selected={mode === 'url'} style={S.tab(mode === 'url')} onClick={() => setMode('url')}>
          {t('tabUrl')}
        </button>
        <button type="button" role="tab" aria-selected={mode === 'quick'} style={S.tab(mode === 'quick')} onClick={() => setMode('quick')}>
          {t('tabQuick')}
        </button>
      </div>

      {mode === 'url' ? (
        <>
          <label style={S.label} htmlFor="hh-url">{t('urlLabel')}</label>
          <div style={S.row}>
            <input
              id="hh-url"
              ref={urlRef}
              style={S.input}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t('urlPlaceholder')}
              inputMode="url"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              enterKeyHint="done"
            />
            <button type="button" style={S.ghost} onClick={() => void paste()}>{t('paste')}</button>
          </div>
          <p style={S.note}>{t('noFetch')}</p>

          <label style={S.label} htmlFor="hh-saw">{t('sawLabel')}</label>
          <textarea id="hh-saw" style={S.area} value={saw} onChange={(e) => setSaw(e.target.value)} placeholder={t('sawHint')} />

          <label style={S.label} htmlFor="hh-url-price">{t('askingPrice')}</label>
          <input
            id="hh-url-price"
            style={S.input}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            inputMode="decimal"
            placeholder="$"
          />
        </>
      ) : (
        <>
          <label style={S.label} htmlFor="hh-title">{t('quickTitle')}</label>
          <input id="hh-title" style={S.input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('quickTitlePlaceholder')} />

          <label style={S.label} htmlFor="hh-price">{t('askingPrice')}</label>
          <input id="hh-price" style={S.input} value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" placeholder="$" />

          <label style={S.label} htmlFor="hh-where">{t('where')}</label>
          <input id="hh-where" style={S.input} value={where} onChange={(e) => setWhere(e.target.value)} placeholder={t('wherePlaceholder')} />

          <span style={S.label}>{t('structure')}</span>
          <div style={S.chipRow}>
            <button type="button" style={S.chip(structure === '')} onClick={() => setStructure('')}>{t('structureUnset')}</button>
            {STRUCTURE_TYPES.map((kind) => (
              <button key={kind} type="button" style={S.chip(structure === kind)} onClick={() => setStructure(kind)}>
                {STRUCTURE_LABEL[locale][kind] ?? kind}
              </button>
            ))}
          </div>

          <label style={S.label} htmlFor="hh-seller">{t('sellerName')}</label>
          <input id="hh-seller" style={S.input} value={sellerName} onChange={(e) => setSellerName(e.target.value)} autoComplete="off" />

          <label style={S.label} htmlFor="hh-phone">{t('sellerPhone')}</label>
          <input id="hh-phone" style={S.input} value={sellerPhone} onChange={(e) => setSellerPhone(e.target.value)} inputMode="tel" autoComplete="off" />

          <label style={S.label} htmlFor="hh-notes">{t('notes')}</label>
          <textarea id="hh-notes" style={S.area} value={notes} onChange={(e) => setNotes(e.target.value)} />

          <span style={S.label}>{t('photos')}</span>
          <button type="button" style={S.ghost} onClick={() => fileRef.current?.click()}>
            <Icon name="camera" size={20} />
            {busy ? t('photoWorking') : t('addPhoto')}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            // `capture` is deliberately absent: on a phone this keeps BOTH the
            // camera and the library available, and most quick adds are a
            // screenshot that is already in the library.
            style={{ display: 'none' }}
            onChange={(e) => {
              void addPhotos(e.target.files)
              e.target.value = ''
            }}
          />
          <div style={S.thumbs}>
            {thumbs.map((thumb) => (
              <div key={thumb.key} style={{ position: 'relative' }}>
                <img src={thumb.url} alt={thumb.name} style={S.thumb} />
                <button
                  type="button"
                  aria-label={t('remove')}
                  onClick={() => void dropPhoto(thumb.key)}
                  style={{
                    position: 'absolute', top: -6, right: -6, width: 28, height: 28, borderRadius: 999,
                    border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', cursor: 'pointer',
                    display: 'grid', placeItems: 'center',
                  }}
                >
                  <Icon name="close" size={14} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <button type="button" style={S.primary(busy)} onClick={() => void submit()} disabled={busy}>
        {busy ? t('saving') : t('save')}
      </button>

      <button
        type="button"
        style={{ ...S.ghost, width: '100%', marginTop: 10, justifyContent: 'center' }}
        onClick={() => navigate('/feed')}
      >
        {t('done')}
      </button>
    </div>
  )
}
